import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

export const OFFER_STATUSES = [
  'draft', 'pending_approval', 'approved', 'sent', 'accepted',
  // `rejected` ADAYIN reddi, `approval_rejected` ONAYCININ reddidir — ikisi AYRI
  // durumlardır ve süzgeçte de ayrı görünürler (aynı kutuya konursa "teklifimizi kaç
  // aday reddetti" sorusu yanlış cevaplanır).
  'rejected', 'approval_rejected', 'expired', 'withdrawn', 'negotiating', 'rescinded',
];
// FE-34 — küme KADRO TALEBİNİNKİYLE (ats-requisitions/config CONTRACT_TYPES) ve backend
// AtsOfferDataAccess.AllowedContractTypes ile BİREBİR aynı olmalı. Eksik `permanent_part_time`
// yüzünden yarı zamanlı kadrolar süzgeçte hiç görünmüyordu.
export const OFFER_CONTRACT_TYPES = [
  'permanent_full_time', 'permanent_part_time', 'fixed_term', 'contractor', 'internship',
];
export const OFFER_SIGNATURE_PROVIDERS = ['docusign', 'kep', 'adobe_sign', 'wet_ink_fallback'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const boolFilter = (label, t) => ({
  filter: true, sort: false, filterType: 'custom',
  filterOptions: {
    defaultOp: 'eq',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0] !== undefined ? filterList[index][0] : '';
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              filterList[index] = v === '' ? [] : [v];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
            <MenuItem value="true">{t('common.yes')}</MenuItem>
            <MenuItem value="false">{t('common.no')}</MenuItem>
          </Select>
        </FormControl>
      );
    },
  },
});

const useAtsOffersApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/ats-offers?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const action = useCallback(async (id, slug, body) => {
    try {
      const res = await api.post(`/v1/ats-offers/${id}/${slug}`, body || {});
      refresh();
      return res.data?.data ?? true;
    } catch { return false; }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/ats-offers/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const statusLabel = (s) => t(`ats.offer.status.${s}`, { defaultValue: s });
    const contractLabel = (s) => t(`ats.offer.contractType.${s}`, { defaultValue: s });
    const providerLabel = (s) => t(`ats.offer.signatureProvider.${s}`, { defaultValue: s });

    // Sıralama: backend AtsOfferDataAccess orderBy switch'i createdate | status | basesalary —
    // `sortKey` bunlara eşler; diğer kolonlar `sort: false`.
    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'candidateFullName',
        label: t('ats.offer.candidate'),
        options: {
          filter: true, sort: false,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'candidateName[like]' },
        },
      },
      {
        name: 'jobPostingTitle',
        label: t('ats.offer.jobPosting'),
        options: { filter: false, sort: false },
      },
      numericFilterColumn('version', t('ats.offer.version'), { decimals: 0, sort: false }),
      {
        name: 'status',
        label: t('ats.offer.statusLabel'),
        options: {
          ...multiSelectFilter(t('ats.offer.statusLabel'), OFFER_STATUSES, statusLabel),
          sortKey: 'status',
          customBodyRender: (v) => statusLabel(v),
        },
      },
      numericFilterColumn('baseSalary', t('ats.offer.baseSalary'), { decimals: 2, sortKey: 'basesalary' }),
      {
        name: 'currency',
        label: t('ats.offer.currency'),
        options: { filter: false, sort: false },
      },
      {
        name: 'contractType',
        label: t('ats.offer.contractTypeLabel'),
        options: {
          ...multiSelectFilter(t('ats.offer.contractTypeLabel'), OFFER_CONTRACT_TYPES, contractLabel),
          sort: false,
          customBodyRender: (v) => v ? contractLabel(v) : '-',
        },
      },
      dateFilterColumn('startDate', t('ats.offer.startDate'), lang, { sort: false }),
      dateFilterColumn('responseDeadline', t('ats.offer.responseDeadline'), lang, { sort: false }),
      {
        name: 'signatureProvider',
        label: t('ats.offer.signatureProviderLabel'),
        options: {
          ...multiSelectFilter(t('ats.offer.signatureProviderLabel'), OFFER_SIGNATURE_PROVIDERS, providerLabel),
          sort: false,
          customBodyRender: (v) => v ? providerLabel(v) : '-',
        },
      },
      {
        name: 'bandViolationApprovalRequired',
        label: t('ats.offer.bandViolation'),
        options: {
          ...boolFilter(t('ats.offer.bandViolation'), t),
          // Gerekçe (below_min | above_max | currency_mismatch) hücre ipucu olur (EO-02).
          customBodyRender: (v, tableMeta) => {
            if (!v) return '-';
            const reason = data[tableMeta?.rowIndex]?.bandViolationReason;
            const title = reason
              ? t(`ats.offer.bandReason.${reason}`, { defaultValue: t('ats.offer.bandViolation') })
              : t('ats.offer.bandViolation');
            return <span title={title}>⚠</span>;
          },
        },
      },
      {
        // Atanmış onaycı (FE-20). Adı sunucu KİMLİK AİLESİYLE çözer; arayüz ham kimliği
        // çizmez. Süzgeç/sıralama yok — arka uçta karşılığı olmayan bir süzgeç sessiz no-op
        // olurdu (repo hafızası `reference_datatable_filterkey_dead`).
        name: 'approverName',
        label: t('ats.offer.approver'),
        options: {
          filter: false, sort: false,
          customBodyRender: (v) => v || '-',
        },
      },
      dateFilterColumn('createDate', t('ats.offer.createDate'), lang, { sortKey: 'createdate' }),
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, action, remove };
};

export default useAtsOffersApi;
